import React, {useCallback, useEffect, useMemo, useRef} from 'react';

import classnames from 'classnames';
import {Map} from 'immutable';
import {Link} from 'react-router';

import {Grid, Icon, TabNavigation} from '@albert-io/atomic';
import {resource} from '@albert-io/json-api-framework/request/builder';

import {callAction} from 'client/framework';
import appStore from 'client/AppStore';
import {history} from 'client/history';
import sessionStore from 'client/Session/SessionStore';
import masqueradeStore from 'generic/Masquerade/Masquerade.store';
import navActions from 'client/GlobalNavigation/GlobalNavigationActions';
import ErrorPage from 'client/static/Error/ErrorPage.react';
import {ERROR_TYPES} from 'client/constants';
import {HeroUnit} from 'client/generic/HeroUnit/HeroUnit.react';
import Button from 'sg/Button/Button.react';
import {
  getActivePathFromPathname,
  getSubgroupItemsByActivePath
} from 'generic/SubjectMenuRenderer/subjectMenuData.utils';
import makeConstants from 'lib/makeConstants';
import useMandarkQueries from 'lib/hooks/useMandarkQueries';
import LoadingIndicator from 'generic/LoadingIndicator.react';

import {getSubjectViewLinks} from '../getSubjectViewLinks';

import {useSubjectViewQuery} from './SubjectView.queries.generated';
import {createSubjectViewAdapter} from './subjectAdapter';
import type {SubjectAdapter} from './subjectAdapter';
import {subjectViewActions as svActions} from './SubjectView.actions';
import subjectViewStore from './SubjectView.store';
import BlockedAccessModal from './BlockedAccessModal.react';

import '../assets/subject-view.scss';

function getUserQuery() {
  return sessionStore.isStudent()
    ? resource('users_v2')
        .mandarkEndpoint(['users_v2', sessionStore.getUserId()])
        .include('student_v2.student_classrooms_v1')
    : resource('users_v2')
        .mandarkEndpoint(['users_v2', sessionStore.getUserId()])
        .include('teacher_v1')
        .withMeta('teacher_v1');
}

interface SubjectViewProps {
  children?: React.ReactNode;
  location: {
    pathname: string;
    search: string;
  };
  params: {
    slug: string;
  };
}

function getBackButton() {
  const prevPathname = subjectViewStore.getBackButtonPath();
  const activePath = getActivePathFromPathname(prevPathname, '/subjects');
  // getActivePathFromPathname returns a string[]; getSubgroupItemsByActivePath only
  // reads the first two entries ([domain, group]) so the tuple cast is safe.
  const items = getSubgroupItemsByActivePath(activePath as [string, string], false);
  const referredFromCourseLibrary = !items.isEmpty();
  if (referredFromCourseLibrary) {
    return <Button to={prevPathname}>Courses</Button>;
  }
  return null;
}

function getLinks(subject: SubjectAdapter, slug: string) {
  const subjectGuideTypes = makeConstants('assessment', 'practice', 'free_response');
  const subjectGuides = subject
    .getGuides()
    .toArray()
    .map((guide) => guide.getGuideType());
  const navConfig = Map({
    studyGuide: subjectGuides.includes(subjectGuideTypes.practice),
    FRQ: subjectGuides.includes(subjectGuideTypes.free_response),
    assessments: subjectGuides.includes(subjectGuideTypes.assessment),
    FAQ: sessionStore.hasContentManagerAccess() || !subject.getSubjectFaqs().isEmpty()
  });
  return getSubjectViewLinks({subjectSlug: slug, subjectId: subject.getId(), navConfig});
}

const SubjectViewComponent = ({children, location, params}: SubjectViewProps) => {
  const {slug} = params;

  // GQL query for subject data
  const {data, loading: gqlLoading} = useSubjectViewQuery({variables: {slug}});
  const gqlSubject = data?.subjects?.results?.[0];
  const subject = useMemo(
    () => (gqlSubject ? createSubjectViewAdapter(gqlSubject) : null),
    [gqlSubject]
  );

  // Mandark bridge for user query (user migration is a separate issue)
  const userQueries = sessionStore.hasValidSession() ? [getUserQuery()] : [];
  const {isReady: userReady, resources: userResources} = useMandarkQueries(userQueries);
  const user =
    sessionStore.hasValidSession() && userReady && userResources.length > 0
      ? userResources[0]
      : undefined;

  const loading = gqlLoading || (sessionStore.hasValidSession() && !userReady);

  const prevSearchRef = useRef(location.search);
  const subjectIdRef = useRef<string | null>(null);

  useEffect(() => {
    subjectIdRef.current = subject ? subject.getId() : null;
  }, [subject]);

  // Save initial back-button path and set upgrade button on mount
  useEffect(() => {
    callAction(navActions.SET_SHOULD_SHOW_UPGRADE_BUTTON_NO_EMIT, true);

    const {prevRouterProps} = appStore;
    // prevRouterProps is an untyped Record<string, unknown>; narrow the router location shape.
    const prevLocation = prevRouterProps.location as {pathname?: string} | undefined;
    const prevPathname = prevLocation ? (prevLocation.pathname ?? '') : '';
    callAction(svActions.SET_BACK_BUTTON_PATH, prevPathname);

    callAction(navActions.SET_SHOULD_SHOW_UPGRADE_BUTTON, true);

    return () => {
      callAction(navActions.SET_SHOULD_SHOW_UPGRADE_BUTTON, false);
      callAction(svActions.RESET_STORE);
      registerScrollY();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push subject to the SubjectViewStore when it's available
  useEffect(() => {
    if (subject) {
      callAction(svActions.SET_ACTIVE_SUBJECT, subject);
    }
  }, [subject]);

  // Redirect to base route if no children (matches original componentDidMount logic)
  useEffect(() => {
    if (!loading && subject && !children) {
      const cleanPathname = location.pathname.replace(/\/$/, '');
      history.replace({pathname: cleanPathname});
    }
  }, [loading, subject, children, location.pathname]);

  // Scroll restore on initial mount and search changes
  useEffect(() => {
    if (!loading && subject) {
      const subjectId = subject.getId();
      if (subjectViewStore.getScrollPosition(subjectId)) {
        scrollToPrevY(subjectId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, !!subject]);

  useEffect(() => {
    if (!loading && subject && location.search !== prevSearchRef.current) {
      prevSearchRef.current = location.search;
      const subjectId = subject.getId();
      if (subjectViewStore.getScrollPosition(subjectId)) {
        scrollToPrevY(subjectId);
      }
    }
  }, [loading, subject, location.search]);

  const registerScrollY = useCallback(() => {
    const subjectId = subjectIdRef.current;
    if (subjectId) {
      callAction(
        svActions.SET_SCROLL_POSITION,
        {
          subjectId,
          scrollYPosition: global.scrollY
        },
        undefined
      );
    }
  }, []);

  const scrollToPrevY = (subjectId: string) => {
    global.requestAnimationFrame(() => {
      global.requestAnimationFrame(() => {
        global.scrollTo(0, subjectViewStore.getScrollPosition(subjectId));
        callAction(
          svActions.SET_SCROLL_POSITION,
          {
            subjectId,
            scrollYPosition: null
          },
          undefined
        );
      });
    });
  };

  if (loading) {
    return <LoadingIndicator />;
  }

  if (!subject) {
    return (
      <ErrorPage errorCode={ERROR_TYPES.NOT_FOUND} errorText='This subject could not be found.' />
    );
  }

  if (subject.isHidden() && !sessionStore.hasContentManagerAccess()) {
    return (
      <ErrorPage
        errorCode={ERROR_TYPES.UNAUTHORIZED}
        errorText='This subject has been unpublished.'
      />
    );
  }

  const curriculumAreaSlug = subject.getCurriculumAreaSlug();
  const backgroundColor = `var(--color-background-domain-${curriculumAreaSlug})`;
  const route = location.pathname.split('/').pop();
  const isStudyGuide = route === 'free-response' || route === slug;
  const links = getLinks(subject, slug);

  return (
    <div
      className={classnames('subject-view-wrapper', {
        'subject-view-wrapper--study-guide': isStudyGuide
      })}
    >
      {masqueradeStore.isMasquerading() && <BlockedAccessModal />}
      <HeroUnit backButton={getBackButton()} backgroundColor={backgroundColor}>
        <HeroUnit.Icon src={subject.getLogoLocation().href} />
        <h1>{subject.getName()}</h1>
      </HeroUnit>
      <TabNavigation>
        {links
          .map((item) => {
            // Immutable's List.map types the callback value as possibly undefined; it
            // never is at runtime, but guard so the narrowing below is type-safe.
            if (!item) {
              return null;
            }
            // Map<string, unknown> field access: these are string-valued links.
            const name = String(item.get('name'));
            if (item.get('isExternal')) {
              return (
                <TabNavigation.Tab
                  key={name}
                  as='a'
                  href={String(item.get('blogUrl'))}
                  target='_blank'
                  rel='noopener noreferrer'
                  aria-label={`${name} (opens in new tab)`}
                  isActive={false}
                >
                  <span className='flex items-center gap-x1'>
                    {name}
                    <Icon icon={['far', 'arrow-up-right-from-square']} />
                  </span>
                </TabNavigation.Tab>
              );
            }
            const to = String(item.get('to'));
            return (
              <TabNavigation.Tab key={name} isActive={location.pathname === to} as={Link} to={to}>
                {name}
              </TabNavigation.Tab>
            );
          })
          .toArray()}
      </TabNavigation>
      <Grid.Container className='subject-view-content mt-x2 mb-x2'>
        <Grid.Row justify='center' className='m-x0 w-full'>
          <Grid.Col all={12} className='m-x0 w-full'>
            {children &&
              React.cloneElement(children as React.ReactElement, {
                subject,
                user,
                onSearchCallback: registerScrollY
              })}
          </Grid.Col>
        </Grid.Row>
      </Grid.Container>
    </div>
  );
};

export {SubjectViewComponent as SubjectView};
